'use client';
/** R14b 시트·팝오버 공용 — 포커스 가두기 + Esc 닫기. 열릴 때 첫 입력(없으면 첫 버튼)으로, 닫히면 호출부가 트리거로 돌려준다. */
import { useEffect, type RefObject } from 'react';

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

export function useFocusTrap(open: boolean, ref: RefObject<HTMLElement | null>, onClose: () => void, initial?: string): void {
  useEffect(() => {
    if (!open) return;
    const el = ref.current;
    if (!el) return;
    const first = (initial ? el.querySelector<HTMLElement>(initial) : null) ?? el.querySelector<HTMLElement>('input') ?? el.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const onKey = (e: KeyboardEvent) => {
      // 안쪽에 열린 다른 대화상자(검색 시트 안 지역 칩 패널)가 포커스를 가졌으면 그쪽이 처리한다.
      const owner = (document.activeElement as HTMLElement | null)?.closest?.('[role="dialog"]');
      if (owner && owner !== el && el.contains(owner)) return;
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const list = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null || n === document.activeElement);
      if (!list.length) return;
      const a = list[0];
      const z = list[list.length - 1];
      if (e.shiftKey && (document.activeElement === a || !el.contains(document.activeElement))) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && (document.activeElement === z || !el.contains(document.activeElement))) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, ref, onClose, initial]);
}
